import React, { ReactElement, useEffect } from "react";

import { ContentModule } from "utils/orm";
import { ContentMapper } from "components/ContentMapper";
import { initLazyImages } from "utils/images";
import { getObserver } from "utils/observer";
import { triggerSnowplowImpressionEvent, triggerSnowplowVideoEvent } from "@themuse/snowplow-js-client";

interface ArticleBodyProps {
    articleContentModules: ContentModule[];
    blockHeadingPrefix?: string;
    listBuilder?: ContentModule[];
    postListBodyContent?: ContentModule[];
    templateName: string;
    tocEntities: Array<{ heading: string; id: number }>;
}

/**
 * Tracks video impressions by observing the given HTMLDivElement element.
 *
 * @param el - The HTMLDivElement element to observe for video impressions.
 * @returns void
 */
const trackVideoImpressions = (el: HTMLDivElement) => {
    const impressionObserver = getObserver(triggerSnowplowImpressionEvent);
    impressionObserver.observe(el);
};

// only video elements that have not been initialized with tracking props.
const selector = "div[itemtype=\"https://schema.org/VideoObject\"]:not([data-tracking-props=\"*\"])";

/**
 * Sets the tracking properties for a video wrapper element.
 *
 * @param {HTMLDivElement} wrapper - The video wrapper element.
 * @returns {void}
 */
const setTrackingProps = (wrapper: HTMLDivElement): void => {
    const scriptEl: HTMLScriptElement = wrapper.querySelector("script");
    const id: string = scriptEl.src.split("/players/")[1].split("-")[0];
    const metaEl: HTMLMetaElement = wrapper.querySelector("meta[itemprop=\"name\"]");
    const title: string = metaEl.content;
    const content = JSON.stringify({
        sp_impression_component: "insert",
        sp_impression_insert_type: "media",
        sp_impression_position: "in_article",
        sp_media_type: "video",
        sp_media_name: title,
        sp_media_provider_key: id,
        sp_media_provider_name: "jwplayer",
    });
    wrapper.setAttribute("data-tracking-props", content);
    wrapper.setAttribute("data-tracking-event-type", "impression");
};

/**
 * Renders the body of an article.
 *
 * @param {ArticleBodyProps} props - The props for the ArticleBody component.
 * @param {ContentModule[]} props.articleContentModules - The content modules for the article.
 * @param {string} [props.blockHeadingPrefix] - The prefix for block headings.
 * @param {ContentModule[]} [props.listBuilder] - The content modules for the list builder.
 * @param {ContentModule[]} [props.postListBodyContent] - The content modules for the post list body.
 * @param {string} props.templateName - The template name.
 * @param {Array<{ heading: string; id: number }>} props.tocEntities - The table of contents entities.
 * @returns {JSX.Element} The rendered ArticleBody component.
 */
const ArticleBody = (props: ArticleBodyProps): ReactElement => {
    const {
        articleContentModules,
        blockHeadingPrefix,
        listBuilder,
        postListBodyContent,
        templateName,
        tocEntities
    } = props;
    const contentProps = { modules: articleContentModules, templateName, tocEntities };

    const listBuilderProps = {
        modules: listBuilder, templateName, hasBullets: true, prefix: blockHeadingPrefix
    };
    const postBodyProps = { modules: postListBodyContent, templateName };

    useEffect(() => {
        const elements: NodeList = document.querySelectorAll(selector);
        if (elements.length) {
            Array.from(elements).forEach((el: HTMLDivElement) => {
                el.addEventListener("click", () => triggerSnowplowVideoEvent("play", el), { once: true });
                setTrackingProps(el);
                trackVideoImpressions(el);
            });
        }

        initLazyImages();
    }, []);

    return (
        <>
            {ContentMapper(contentProps)}
            {listBuilder && ContentMapper(listBuilderProps)}
            {postListBodyContent && ContentMapper(postBodyProps)}
        </>
    );
};

export { ArticleBody };
